import { useState } from 'react';
import { useMutation } from './query.js';
import {
  generatePortalQuestion,
  savePortalQuestion,
  ApiError,
  type GeneratedPortalQuestion,
  type PortalTier,
} from './api.js';

/**
 * M11 — "Suggest new question" dialog for a portal (levels-marked 6/9/12-mark,
 * cross-cluster). Mirrors NewQuestionModal but for portals.
 *
 * Flow: pick tier (6/9/12 → path6/conditional/final) + command_word → Generate
 * (POST /api/ai/new-portal-question, NO DB write) → preview the returned draft
 * read-only (the canonical level bands are injected server-side and shown here
 * for context) → Save (POST /api/portal-template, inserts a DRAFT/pending
 * SYNTHETIC row) / Regenerate / Discard.
 *
 * Generation never writes the DB; only Save does. On Save success the parent
 * invalidates the portal caches so the new DRAFT appears.
 */

// Command words valid for extended-response portal questions (§4.7).
const COMMAND_WORD_OPTIONS = ['Analyse', 'Assess', 'Discuss', 'Evaluate'] as const;

// tier → marks + default command word (binding table §2).
const TIER_OPTIONS: { tier: PortalTier; marks: number; defaultCw: string }[] = [
  { tier: 'path6', marks: 6, defaultCw: 'Analyse' },
  { tier: 'conditional', marks: 9, defaultCw: 'Assess' },
  { tier: 'final', marks: 12, defaultCw: 'Evaluate' },
];

// Canonical Edexcel level bands per mark value (CANONICAL_LEVEL_RUBRICS) — shown
// read-only for context. These are injected server-side; the LLM never authors
// them. Kept here purely for the preview display.
const LEVEL_BANDS: Record<number, { level: number; marks: string }[]> = {
  6: [
    { level: 1, marks: '1-2' },
    { level: 2, marks: '3-4' },
    { level: 3, marks: '5-6' },
  ],
  9: [
    { level: 1, marks: '1-3' },
    { level: 2, marks: '4-6' },
    { level: 3, marks: '7-9' },
  ],
  12: [
    { level: 1, marks: '1-4' },
    { level: 2, marks: '5-8' },
    { level: 3, marks: '9-12' },
  ],
};

function errDetail(e: unknown): string {
  const err = e as ApiError;
  if (err.body && typeof err.body === 'object' && 'detail' in err.body) {
    return String((err.body as Record<string, unknown>).detail);
  }
  return err.message;
}

function defaultCwForTier(tier: PortalTier): string {
  return TIER_OPTIONS.find((t) => t.tier === tier)!.defaultCw;
}

function marksForTier(tier: PortalTier): number {
  return TIER_OPTIONS.find((t) => t.tier === tier)!.marks;
}

export function NewPortalQuestionModal({
  portalKey,
  onClose,
  onSaved,
}: {
  portalKey: string;
  onClose: () => void;
  onSaved: () => void;
}) {
  const [tier, setTier] = useState<PortalTier>('final');
  const [commandWord, setCommandWord] = useState<string>('Evaluate');
  const [draft, setDraft] = useState<GeneratedPortalQuestion | null>(null);

  const marks = marksForTier(tier);

  const generate = useMutation({
    mutationFn: () =>
      generatePortalQuestion({
        portal_key: portalKey,
        tier,
        command_word: commandWord,
      }),
    onSuccess: (res) => setDraft(res.question),
  });

  const save = useMutation({
    mutationFn: () =>
      savePortalQuestion({
        portal_key: portalKey,
        tier,
        command_word: commandWord,
        question: draft!,
      }),
    onSuccess: () => {
      onSaved();
      onClose();
    },
  });

  // When the tier changes, reset the command word to that tier's default so the
  // SME isn't left with a mismatched default (they can still override).
  function onTierChange(next: PortalTier) {
    setTier(next);
    setCommandWord(defaultCwForTier(next));
    setDraft(null);
  }

  const bands = LEVEL_BANDS[marks] ?? [];

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal"
        onClick={(e) => e.stopPropagation()}
        data-testid="modal-new-portal-question"
      >
        <h2 style={{ marginTop: 0 }}>
          Suggest new question — <span style={{ fontFamily: 'var(--mono)' }}>{portalKey}</span>
        </h2>

        <div className="field-grid">
          <div>
            <label>Tier</label>
            <select
              value={tier}
              onChange={(e) => onTierChange(e.target.value as PortalTier)}
              disabled={generate.isPending || save.isPending}
              data-testid="select-new-portal-question-tier"
            >
              <option value="path6">6 (path6)</option>
              <option value="conditional">9 (conditional)</option>
              <option value="final">12 (final)</option>
            </select>
          </div>
          <div>
            <label>Command word</label>
            <select
              value={commandWord}
              onChange={(e) => setCommandWord(e.target.value)}
              disabled={generate.isPending || save.isPending}
              data-testid="select-new-portal-question-command-word"
            >
              {COMMAND_WORD_OPTIONS.map((cw) => (
                <option key={cw} value={cw}>
                  {cw}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="row-actions" style={{ marginTop: 10 }}>
          <button
            className="primary"
            type="button"
            onClick={() => generate.mutate()}
            disabled={generate.isPending || save.isPending}
            data-testid="button-new-portal-question-generate"
          >
            {generate.isPending ? 'Generating…' : draft ? 'Regenerate' : 'Generate'}
          </button>
          <button
            type="button"
            onClick={onClose}
            disabled={save.isPending}
            data-testid="button-new-portal-question-discard"
          >
            Discard
          </button>
          {generate.isError && (
            <span className="err" data-testid="new-portal-question-generate-error">
              Generation failed: {errDetail(generate.error)}
            </span>
          )}
        </div>

        {draft && (
          <div className="read-view" data-testid="new-portal-question-preview" style={{ marginTop: 14 }}>
            <h3 style={{ fontSize: 13, margin: '4px 0' }}>
              Preview ({marks}m · {commandWord})
            </h3>
            <p className="question-stem" style={{ whiteSpace: 'pre-wrap' }}>
              {draft.stem}
            </p>

            <h3 style={{ fontSize: 13, margin: '10px 0 4px' }}>
              Level bands (injected by platform)
            </h3>
            <table className="mark-scheme-table">
              <thead>
                <tr>
                  <th>Level</th>
                  <th>Marks</th>
                </tr>
              </thead>
              <tbody>
                {bands.map((b) => (
                  <tr key={b.level}>
                    <td style={{ fontFamily: 'var(--mono)' }}>L{b.level}</td>
                    <td style={{ fontFamily: 'var(--mono)' }}>{b.marks}</td>
                  </tr>
                ))}
              </tbody>
            </table>

            {draft.indicative_content.length > 0 && (
              <>
                <h3 style={{ fontSize: 13, margin: '10px 0 4px' }}>Indicative content</h3>
                <ul>
                  {draft.indicative_content.map((c, i) => (
                    <li key={i}>{c}</li>
                  ))}
                </ul>
              </>
            )}

            {draft.model_answer && (
              <details className="model-answer" open>
                <summary>Model answer</summary>
                <p style={{ whiteSpace: 'pre-wrap' }}>{draft.model_answer}</p>
              </details>
            )}

            {draft.examiner_traps.length > 0 && (
              <>
                <h3 style={{ fontSize: 13, margin: '10px 0 4px' }}>Examiner traps</h3>
                <ul>
                  {draft.examiner_traps.map((t, i) => (
                    <li key={i}>{t}</li>
                  ))}
                </ul>
              </>
            )}

            {draft.concept_tags.length > 0 && (
              <>
                <h3 style={{ fontSize: 13, margin: '10px 0 4px' }}>Concept tags</h3>
                <div className="chips">
                  {draft.concept_tags.map((tag) => (
                    <span key={tag} className="chip" style={{ fontFamily: 'var(--mono)' }}>
                      {tag}
                    </span>
                  ))}
                </div>
              </>
            )}

            <div className="row-actions" style={{ marginTop: 12 }}>
              <button
                className="primary"
                type="button"
                onClick={() => save.mutate()}
                disabled={save.isPending}
                data-testid="button-new-portal-question-save"
              >
                {save.isPending ? 'Saving…' : 'Save as DRAFT'}
              </button>
              <button
                type="button"
                onClick={() => generate.mutate()}
                disabled={generate.isPending || save.isPending}
                data-testid="button-new-portal-question-regenerate"
              >
                Regenerate
              </button>
              {save.isError && <span className="err">Save failed</span>}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
